import React, { useMemo, useState } from 'react';
import Icon from '../../../components/AppIcon';

/**
 * Validates property data and returns missing critical fields
 */
export const validatePropertyData = (property) => {
  if (!property) return { missingFields: [], warnings: [] };

  const missingFields = [];
  const warnings = [];

  // Critical fields that should always be present
  if (!property?.insurance || parseFloat(property.insurance) === 0) {
    missingFields.push({
      field: 'insurance',
      label: 'Assurance',
      critical: true,
      message: 'Aucune assurance n\'a été saisie. Ce champ est essentiel pour une analyse précise.'
    });
  }

  if (!property?.property_taxes || parseFloat(property.property_taxes) === 0) {
    missingFields.push({
      field: 'property_taxes',
      label: 'Taxes foncières',
      critical: true,
      message: 'Aucune taxe foncière n\'a été saisie. Ce champ est essentiel pour une analyse précise.'
    });
  }

  if (!property?.total_monthly_revenue || parseFloat(property.total_monthly_revenue) === 0) {
    missingFields.push({
      field: 'total_monthly_revenue',
      label: 'Revenus mensuels',
      critical: true,
      message: 'Aucun revenu mensuel n\'a été saisi. Ce champ est essentiel pour calculer le cashflow.'
    });
  }

  // Important but not critical fields
  if (!property?.maintenance || parseFloat(property.maintenance) === 0) {
    warnings.push({
      field: 'maintenance',
      label: 'Maintenance',
      critical: false,
      message: 'Aucun montant de maintenance n\'a été saisi. Il est recommandé d\'inclure ce coût pour une analyse complète.'
    });
  }

  if (!property?.management_fee_percent || parseFloat(property.management_fee_percent) === 0) {
    warnings.push({
      field: 'management_fee_percent',
      label: 'Frais de gestion',
      critical: false,
      message: 'Aucun frais de gestion n\'a été saisi. Il est recommandé d\'inclure ce coût si vous utilisez un gestionnaire.'
    });
  }

  if (!property?.heating || parseFloat(property.heating) === 0) {
    warnings.push({
      field: 'heating',
      label: 'Chauffage',
      critical: false,
      message: 'Aucun coût de chauffage n\'a été saisi. Il est recommandé d\'inclure ce coût pour une analyse complète.'
    });
  }

  // Note: Water (eau) is not validated as it's typically included in property taxes in Quebec

  return { missingFields, warnings };
};

const DataValidationAlert = ({ property, onEdit, mode = 'full' }) => {
  if (!property) return null;

  const { missingFields, warnings } = useMemo(() => validatePropertyData(property), [property]);
  const [expanded, setExpanded] = useState(null); // 'critical' | 'recommended' | null

  const renderCompactGroup = ({
    id,
    title,
    subtitle,
    items,
    icon,
    badgeClass,
    cardClass,
    borderClass,
    itemIcon
  }) => {
    const count = items.length;
    const isOpen = expanded === id;
    const isCritical = id === 'critical';

    return (
      <div className={`border rounded-2xl p-5 shadow-sm ${cardClass} ${borderClass}`}>
        <button
          type="button"
          onClick={() => setExpanded(isOpen ? null : id)}
          className="w-full flex items-start justify-between gap-3 text-left"
        >
          <div className="flex items-start gap-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${badgeClass}`}>
              <Icon name={icon} size={20} className={isCritical ? 'text-destructive' : 'text-primary'} />
            </div>
            <div>
              <p className="font-semibold text-foreground leading-tight">{title}</p>
              <p className="text-sm text-muted-foreground">{subtitle}</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <span
              className={`text-xs font-semibold px-2 py-1 rounded-full border ${
                isCritical
                  ? 'bg-destructive/10 border-destructive/20 text-destructive'
                  : 'bg-primary/10 border-primary/20 text-primary'
              }`}
            >
              {count}
            </span>
            <Icon name={isOpen ? 'ChevronUp' : 'ChevronDown'} size={18} className="text-muted-foreground" />
          </div>
        </button>

        {isOpen && (
          <div className="mt-4">
            {count === 0 ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Icon name="CheckCircle" size={16} className="text-success" />
                <span>Rien à signaler.</span>
              </div>
            ) : (
              <ul className="space-y-2">
                {items.map((item, index) => (
                  <li key={index} className="flex gap-2">
                    <Icon name={itemIcon} size={16} className="text-muted-foreground mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-foreground">{item.label}</p>
                      {item.description && (
                        <p className="text-sm text-muted-foreground">{item.description}</p>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}

            {onEdit && (
              <div className="mt-4 flex items-center justify-between gap-3">
                <p className="text-xs text-muted-foreground">
                  {isCritical
                    ? "Complète ces champs pour éviter un score biaisé."
                    : "Optionnel, mais ça rend les projections plus réalistes."}
                </p>
                <button
                  type="button"
                  onClick={onEdit}
                  className={`inline-flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-semibold border transition ${
                    isCritical
                      ? 'bg-destructive text-destructive-foreground border-destructive/30 hover:opacity-95'
                      : 'bg-primary text-primary-foreground border-primary/30 hover:opacity-95'
                  }`}
                >
                  <Icon name="Pencil" size={16} />
                  {isCritical ? 'Compléter maintenant' : 'Modifier les données'}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  if (mode === 'compact') {
    // In compact mode we only show 2 side-by-side indicators that can expand/collapse.
    // ✅ Ajout d'espacement en dessous via mb-10
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-10">
        {renderCompactGroup({
          id: 'critical',
          title: 'Données critiques',
          subtitle: 'Requis pour une analyse fiable',
          items: missingFields || [],
          icon: 'AlertTriangle',
          badgeClass: 'bg-destructive/10 border border-destructive/20',
          cardClass: 'bg-gradient-to-b from-destructive/10 to-background',
          borderClass: 'border-destructive/20',
          itemIcon: 'XCircle'
        })}

        {renderCompactGroup({
          id: 'recommended',
          title: 'Données recommandées',
          subtitle: 'Améliore la précision et le score',
          items: warnings || [],
          icon: 'Sparkles',
          badgeClass: 'bg-primary/10 border border-primary/15',
          cardClass: 'bg-gradient-to-b from-primary/5 to-background',
          borderClass: 'border-primary/15',
          itemIcon: 'Info'
        })}
      </div>
    );
  }

  if (missingFields.length === 0 && warnings.length === 0) {
    return null;
  }

  return (
    <div className="space-y-3 mb-6">
      {/* Critical missing fields */}
      {missingFields.length > 0 && (
        <div className="bg-destructive/10 border-2 border-destructive/30 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-destructive/20 flex items-center justify-center flex-shrink-0">
              <Icon name="AlertTriangle" size={20} style={{ color: 'var(--color-destructive)' }} />
            </div>
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-destructive mb-2">
                Données critiques manquantes
              </h4>
              <p className="text-sm text-foreground mb-3">
                Certains champs essentiels sont manquants ou à zéro. L'analyse pourrait être incomplète ou inexacte.
              </p>
              <ul className="space-y-2">
                {missingFields.map((field, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <Icon name="XCircle" size={16} className="text-destructive flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <span className="text-sm font-medium text-foreground">{field.label}:</span>
                      <span className="text-sm text-muted-foreground ml-1">{field.message}</span>
                    </div>
                  </li>
                ))}
              </ul>
              {onEdit && (
                <button
                  onClick={onEdit}
                  className="mt-3 text-sm font-medium text-destructive hover:underline flex items-center gap-2"
                >
                  <Icon name="Edit" size={16} />
                  Modifier les données
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Warnings for important but not critical fields */}
      {warnings.length > 0 && (
        <div className="bg-warning/10 border-2 border-warning/30 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-warning/20 flex items-center justify-center flex-shrink-0">
              <Icon name="AlertCircle" size={20} style={{ color: 'var(--color-warning)' }} />
            </div>
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-warning mb-2">
                Données recommandées manquantes
              </h4>
              <p className="text-sm text-foreground mb-3">
                Certains champs recommandés sont manquants ou à zéro. L'analyse sera plus précise si ces données sont incluses.
              </p>
              <ul className="space-y-2">
                {warnings.map((field, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <Icon name="Info" size={16} className="text-warning flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <span className="text-sm font-medium text-foreground">{field.label}:</span>
                      <span className="text-sm text-muted-foreground ml-1">{field.message}</span>
                    </div>
                  </li>
                ))}
              </ul>
              {onEdit && (
                <button
                  onClick={onEdit}
                  className="mt-3 text-sm font-medium text-warning hover:underline flex items-center gap-2"
                >
                  <Icon name="Edit" size={16} />
                  Modifier les données
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataValidationAlert;
